import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Box, Stack, Typography, Button, Avatar, alpha, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import HomeWorkRounded from '@mui/icons-material/HomeWorkRounded';
import SyncAltRounded from '@mui/icons-material/SyncAltRounded';
import TravelExploreRounded from '@mui/icons-material/TravelExploreRounded';
import BedRounded from '@mui/icons-material/BedRounded';
import BathtubRounded from '@mui/icons-material/BathtubRounded';
import SquareFootRounded from '@mui/icons-material/SquareFootRounded';
import StickyNote2Rounded from '@mui/icons-material/StickyNote2Rounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import GustavoAura from '../../../components/GustavoAura';
import { WhatsappStep } from './WhatsappStep';
import { useGetMyWhatsappStatusQuery } from '../../../store/api/usersApi';
import {
  FONT_DISPLAY, FONT_BODY, FONT_TECH, INK, TXT, TXT_DIM,
} from '../../../booster/landing/shared';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';

// Acento de la marca ACTIVA del CRM (white-label del tenant), no el emerald fijo del
// landing público: para Imperium = dorado, para tenants sin white-label = emerald de
// Booster. `CRM_PALETTE` se muta in-place con el color del tenant (applyCrmAccent), así
// el onboarding de Gustavo respeta la paleta de cada tenant igual que el resto del CRM.
// IMPORTANTE: leer el acento EN RENDER, no capturarlo a nivel módulo. `CrmThemeProvider`
// aplica el color del tenant (applyCrmAccent) recién al renderizar el CRM logueado — un
// `const X = P.brand.golden` a nivel módulo se evalúa ANTES y se queda con el emerald viejo
// (por eso Imperium veía verde). Como getters, cada lectura en render trae el color vigente.
const accentC = () => P.brand.golden;
const accentCL = () => P.brand.goldenLight;
const em = (a: number) => alpha(accentC(), a);
const emL = (a: number) => alpha(accentCL(), a);

const MBox = motion(Box);
const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];
const WA_GREEN = '#25D366';

// Estilo EXACTO del botón "Zona de Agentes" (BoosterAuthCta): glass verde aclarado,
// pill, blur, borde esmeralda e inset highlight.
const glassGreenSx = () => ({
  fontFamily: FONT_TECH, textTransform: 'none' as const, fontWeight: 700, letterSpacing: '0.04em',
  borderRadius: 999, color: '#fff',
  background: em(0.22),
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  border: `1px solid ${em(0.55)}`,
  boxShadow: `0 8px 28px ${em(0.35)}, inset 0 1px 0 ${alpha('#fff', 0.15)}`,
  transition: 'all .25s',
  '&:hover': {
    background: em(0.34),
    boxShadow: `0 12px 34px ${em(0.5)}, inset 0 1px 0 ${alpha('#fff', 0.2)}`,
    transform: 'translateY(-1px)',
  },
});

/* ── Tarjetas-resultado (mock, sin fetch) ───────────────────────────────────── */
function ResultShell({ children }: { children: ReactNode }) {
  return (
    <Box sx={{
      mt: 1.25, p: 1.5, borderRadius: 2,
      border: `1px solid ${emL(0.3)}`, bgcolor: alpha('#0c100e', 0.85),
      boxShadow: `0 8px 30px ${em(0.18)}`,
    }}>
      {children}
    </Box>
  );
}
function Chip({ label, color = accentCL() }: { label: string; color?: string }) {
  return (
    <Box component="span" sx={{
      px: 0.85, py: 0.2, borderRadius: 999, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em',
      fontFamily: FONT_TECH, color, bgcolor: alpha(color, 0.14), border: `1px solid ${alpha(color, 0.35)}`,
      whiteSpace: 'nowrap',
    }}>{label}</Box>
  );
}
const metaRow = (icon: ReactNode, txt: string) => (
  <Stack direction="row" spacing={0.4} alignItems="center" sx={{ color: TXT_DIM }}>
    <Box sx={{ display: 'flex', '& svg': { fontSize: 13 } }}>{icon}</Box>
    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11 }}>{txt}</Typography>
  </Stack>
);

function CustomerResult() {
  return (
    <ResultShell>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar sx={{ width: 38, height: 38, bgcolor: alpha(accentC(), 0.18), color: accentCL(), fontFamily: FONT_TECH, fontWeight: 800, fontSize: 13 }}>MF</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography noWrap sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13.5, color: TXT }}>María Fernández</Typography>
            <Chip label="CLIENTE CREADO" />
          </Stack>
          <Typography noWrap sx={{ fontFamily: FONT_BODY, fontSize: 11.5, color: TXT_DIM }}>099 412 588 · busca alquiler en Cordón</Typography>
        </Box>
      </Stack>
    </ResultShell>
  );
}
function PropertyThumb({ children }: { children: ReactNode }) {
  return (
    <Box sx={{
      width: 46, height: 46, borderRadius: 1.25, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg, ${em(0.35)}, ${alpha('#0c100e', 0.9)})`,
      border: `1px solid ${emL(0.25)}`, color: accentCL(),
    }}>{children}</Box>
  );
}
function PropertyResult() {
  return (
    <ResultShell>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <PropertyThumb><HomeWorkRounded sx={{ fontSize: 22 }} /></PropertyThumb>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography sx={{ fontFamily: FONT_TECH, fontWeight: 800, fontSize: 9.5, letterSpacing: '0.05em', color: accentCL() }}>IMP-2026-0512</Typography>
            <Chip label="ACTIVA" />
          </Stack>
          <Typography noWrap sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: TXT }}>Apartamento en Pocitos</Typography>
          <Stack direction="row" spacing={1.25} sx={{ mt: 0.25 }}>
            {metaRow(<BedRounded />, '2')}{metaRow(<BathtubRounded />, '1')}{metaRow(<SquareFootRounded />, '68 m²')}
          </Stack>
        </Box>
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 14, color: accentCL() }}>USD 185.000</Typography>
      </Stack>
    </ResultShell>
  );
}
function StatusResult({ reduced }: { reduced: boolean }) {
  return (
    <ResultShell>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <PropertyThumb><HomeWorkRounded sx={{ fontSize: 22 }} /></PropertyThumb>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT_TECH, fontWeight: 800, fontSize: 9.5, letterSpacing: '0.05em', color: accentCL() }}>IMP-2026-0512</Typography>
          <Typography noWrap sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: TXT }}>Apartamento en Pocitos</Typography>
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box component="span" sx={{ fontFamily: FONT_TECH, fontSize: 10, color: TXT_DIM, textDecoration: 'line-through' }}>Activa</Box>
          <ArrowForwardRounded sx={{ fontSize: 13, color: TXT_DIM }} />
          <MBox
            initial={reduced ? false : { scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: reduced ? 0 : 0.35, duration: 0.5, ease: EASE }}
          >
            <Chip label="RESERVADA" color="#F59E0B" />
          </MBox>
        </Stack>
      </Stack>
    </ResultShell>
  );
}
function BolsaResult() {
  return (
    <ResultShell>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <PropertyThumb><TravelExploreRounded sx={{ fontSize: 22 }} /></PropertyThumb>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography sx={{ fontFamily: FONT_TECH, fontWeight: 800, fontSize: 9.5, letterSpacing: '0.05em', color: accentCL() }}>IMP-2026-1144</Typography>
            <Chip label="BOLSA DE LA RED" color="#818CF8" />
          </Stack>
          <Typography noWrap sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: TXT }}>Local comercial · Aguada</Typography>
          <Stack direction="row" spacing={1.25} sx={{ mt: 0.25 }}>{metaRow(<SquareFootRounded />, '570 m²')}</Stack>
        </Box>
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 13, color: accentCL() }}>$U 120.000<Box component="span" sx={{ fontSize: 9, color: TXT_DIM }}>/mes</Box></Typography>
      </Stack>
    </ResultShell>
  );
}

function PropertyMatchResult() {
  return (
    <ResultShell>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <PropertyThumb><HomeWorkRounded sx={{ fontSize: 22 }} /></PropertyThumb>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography sx={{ fontFamily: FONT_TECH, fontWeight: 800, fontSize: 9.5, letterSpacing: '0.05em', color: accentCL() }}>IMP-2026-0388</Typography>
              <Chip label="MATCHEA" />
            </Stack>
            <Typography noWrap sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: TXT }}>Apto 2 dorm · Cordón</Typography>
            <Stack direction="row" spacing={1.25} sx={{ mt: 0.25 }}>{metaRow(<BedRounded />, '2')}{metaRow(<BathtubRounded />, '1')}</Stack>
          </Box>
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 13, color: accentCL() }}>$U 32.000<Box component="span" sx={{ fontSize: 9, color: TXT_DIM }}>/mes</Box></Typography>
        </Stack>
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ pt: 0.85, borderTop: `1px solid ${alpha('#fff', 0.07)}` }}>
          <StickyNote2Rounded sx={{ fontSize: 13, color: accentCL() }} />
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 10.5, color: TXT_DIM }}>Nota agregada a la ficha de María Fernández</Typography>
        </Stack>
      </Stack>
    </ResultShell>
  );
}

/* ── Capacidades ────────────────────────────────────────────────────────────── */
type Turn =
  | { who: 'agent'; text: string }
  | { who: 'gustavo'; text?: string; card?: (p: { reduced: boolean }) => ReactNode; chips?: string[] };
type Capability = {
  key: string; icon: ReactNode; eyebrow: string; title: string; body: string; turns: Turn[];
};
const CAPS: Capability[] = [
  { key: 'contact', icon: <PersonAddAlt1Rounded />, eyebrow: 'CLIENTES',
    title: 'Cargá clientes hablando', body: 'Le dictás un cliente y Gustavo lo guarda, lo cruza con tu inventario y la bolsa, y te deja la nota en su ficha.',
    turns: [
      { who: 'agent', text: 'Guardá a María Fernández, 099 412 588, busca alquiler 2 dorm en Cordón, hasta $35.000.' },
      { who: 'gustavo', text: 'Listo, creé a María Fernández y anoté lo que busca.', card: () => <CustomerResult /> },
      { who: 'gustavo', text: '¿Le busco propiedades que matcheen con eso?', chips: ['Sí, buscá', 'Más tarde'] },
      { who: 'agent', text: 'Sí, dale.' },
      { who: 'gustavo', text: 'Encontré este apto en Cordón que encaja. Le dejé una nota en su ficha con el match.', card: () => <PropertyMatchResult /> },
    ] },
  { key: 'property', icon: <HomeWorkRounded />, eyebrow: 'INVENTARIO',
    title: 'Captás en una frase', body: 'Una propiedad nueva, lista para gestionar y publicar.',
    turns: [
      { who: 'agent', text: 'Cargá un apartamento en Pocitos, 2 dormitorios, 1 baño, 68 m², USD 185.000.' },
      { who: 'gustavo', text: 'Creé la propiedad IMP-2026-0512 en Pocitos. ¿La activo o la dejo en borrador?', card: () => <PropertyResult /> },
    ] },
  { key: 'status', icon: <SyncAltRounded />, eyebrow: 'PIPELINE',
    title: 'Movés el estado sin entrar a la ficha', body: 'Reservás, vendés o alquilás con un mensaje.',
    turns: [
      { who: 'agent', text: 'Marcá la IMP-2026-0512 como reservada.' },
      { who: 'gustavo', text: 'Actualicé el estado a Reservada y lo registré en el historial.', card: (p) => <StatusResult reduced={p.reduced} /> },
    ] },
  { key: 'bolsa', icon: <TravelExploreRounded />, eyebrow: 'BOLSA DE PROPIEDADES',
    title: 'Toda la red, en un mensaje', body: 'Encontrá propiedades de otras inmobiliarias para cerrar en conjunto.',
    turns: [
      { who: 'agent', text: 'Buscame un local en Aguada para alquilar.' },
      { who: 'gustavo', text: 'Encontré un local de 570 m² en Aguada, compartido en la bolsa de la red.', card: () => <BolsaResult /> },
    ] },
];

/* ── Mock de conversación (guión de turnos que se revelan al activar el paso) ── */
function MockExchange({ cap, active, reduced }: { cap: Capability; active: boolean; reduced: boolean }) {
  const turns = cap.turns;
  const [visible, setVisible] = useState(reduced ? turns.length : 0);
  const [typing, setTyping] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!active) { setVisible(reduced ? turns.length : 0); setTyping(false); return; }
    if (reduced) { setVisible(turns.length); setTyping(false); return; }
    setVisible(0); setTyping(false);
    const timers: ReturnType<typeof setTimeout>[] = [];
    let t = 450;
    turns.forEach((turn, idx) => {
      if (turn.who === 'gustavo') {
        timers.push(setTimeout(() => setTyping(true), t)); t += 950;
        timers.push(setTimeout(() => { setTyping(false); setVisible(idx + 1); }, t)); t += 700;
      } else {
        timers.push(setTimeout(() => setVisible(idx + 1), t)); t += 750;
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [active, reduced, cap.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autoscroll al último mensaje a medida que aparecen.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: reduced ? 'auto' : 'smooth' });
  }, [visible, typing, reduced]);

  return (
    <Box sx={{
      width: '100%', maxWidth: 420, borderRadius: 3, overflow: 'hidden',
      border: `1px solid ${alpha('#fff', 0.08)}`, bgcolor: alpha('#0a0d0c', 0.6),
      backdropFilter: 'blur(12px)', boxShadow: `0 30px 80px ${alpha('#000', 0.5)}`,
    }}>
      {/* header del chat (estilo app de mensajería: avatar grande) */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.75, py: 1.4, borderBottom: `1px solid ${alpha('#fff', 0.06)}`, bgcolor: alpha('#0c100e', 0.85) }}>
        <GustavoAura size={42} ring={2} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: FONT_TECH, fontSize: 14, fontWeight: 700, color: TXT, lineHeight: 1.15 }}>Gustavo</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accentCL(), boxShadow: `0 0 6px ${accentCL()}` }} />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: TXT_DIM }}>en línea</Typography>
          </Stack>
        </Box>
      </Stack>

      {/* cuerpo */}
      <Stack ref={bodyRef} spacing={1.25} sx={{ p: 1.5, minHeight: 244, maxHeight: { xs: 320, md: 384 }, overflowY: 'auto', '&::-webkit-scrollbar': { width: 0 } }}>
        {turns.slice(0, visible).map((turn, idx) => (
          <MBox key={idx}
            initial={reduced ? false : { opacity: 0, y: 8, x: turn.who === 'agent' ? 16 : 0 }}
            animate={{ opacity: 1, y: 0, x: 0 }} transition={{ duration: 0.45, ease: EASE }}
            sx={{ alignSelf: turn.who === 'agent' ? 'flex-end' : 'flex-start', maxWidth: '90%' }}>
            {turn.who === 'agent' ? (
              <Box sx={{ px: 1.4, py: 1, borderRadius: '14px 14px 4px 14px', background: `linear-gradient(135deg, ${accentC()}, ${accentCL()})`, color: '#04140e', fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>{turn.text}</Box>
            ) : (
              <>
                {turn.text && <Box sx={{ px: 1.4, py: 1, borderRadius: '14px 14px 14px 4px', bgcolor: alpha('#fff', 0.05), color: TXT, fontFamily: FONT_BODY, fontSize: 13, lineHeight: 1.45, width: 'fit-content', maxWidth: '100%' }}>{turn.text}</Box>}
                {turn.card && turn.card({ reduced })}
                {turn.chips && (
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.85 }}>
                    {turn.chips.map((c, i) => (
                      <Box key={c} sx={{ px: 1.3, py: 0.5, borderRadius: 999, fontFamily: FONT_BODY, fontSize: 11.5, fontWeight: 600, border: `1px solid ${i === 0 ? emL(0.5) : alpha('#fff', 0.14)}`, bgcolor: i === 0 ? em(0.16) : 'transparent', color: i === 0 ? accentCL() : TXT_DIM }}>{c}</Box>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </MBox>
        ))}
        {typing && (
          <MBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} sx={{ alignSelf: 'flex-start' }}>
            <Stack direction="row" spacing={0.5} sx={{ px: 1.4, py: 1.1, borderRadius: '14px 14px 14px 4px', bgcolor: alpha('#fff', 0.05), width: 'fit-content' }}>
              {[0, 1, 2].map((i) => (
                <MBox key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TXT_DIM }} animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }} transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }} />
              ))}
            </Stack>
          </MBox>
        )}
      </Stack>
    </Box>
  );
}

/* ── Componente principal ───────────────────────────────────────────────────── */
export function GustavoOnboarding({ name, onFinish }: { name?: string; onFinish: (openChat: boolean) => void }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const reduced = !!useReducedMotion();
  const [step, setStep] = useState(0); // 0 = intro, 1..N = caps, N+1 = recap, N+2 = whatsapp (cierre)
  const total = CAPS.length + 3;
  const isIntro = step === 0;
  const isWhatsapp = step === total - 1;     // última: configurar WhatsApp
  const isRecap = step === total - 2;        // "Eso es solo el arranque" → botón Configurar
  const capIdx = step - 1;
  const cap = step >= 1 && step <= CAPS.length ? CAPS[capIdx] : null;

  // Los botones de cierre de la última diapo aparecen recién cuando el agente
  // tiene su WhatsApp cargado (si quiere salir sin hacerlo, usa "Saltar").
  const { data: waStatus } = useGetMyWhatsappStatusQuery();
  const waReady = !!waStatus?.configured;
  const waMsg = name
    ? `Hola Gustavo, soy ${name} y quiero saber qué podés hacer?`
    : 'Hola Gustavo, quiero saber qué podés hacer?';
  const waChatLink = `https://wa.me/${(waStatus?.gustavoNumber ?? '').replace(/\D/g, '')}?text=${encodeURIComponent(waMsg)}`;

  const next = () => setStep((s) => Math.min(total - 1, s + 1));

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 2000,
      bgcolor: INK, color: TXT, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* glow de fondo */}
      <MBox aria-hidden sx={{ position: 'absolute', top: '-20%', left: '50%', width: 900, height: 900, transform: 'translateX(-50%)', borderRadius: '50%', background: `radial-gradient(circle, ${em(0.16)}, transparent 62%)`, pointerEvents: 'none' }}
        animate={reduced ? undefined : { opacity: [0.6, 1, 0.6], scale: [1, 1.06, 1] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} />

      {/* topbar: progreso + saltar */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ px: { xs: 2.5, md: 5 }, pt: { xs: 2.5, md: 3.5 }, position: 'relative', zIndex: 1 }}>
        <Stack direction="row" spacing={0.75} sx={{ flex: 1, maxWidth: 360 }}>
          {Array.from({ length: total }).map((_, i) => (
            <Box key={i} sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: alpha('#fff', 0.12), overflow: 'hidden' }}>
              <MBox sx={{ height: '100%', borderRadius: 2, background: `linear-gradient(90deg, ${accentC()}, ${accentCL()})` }}
                initial={false} animate={{ width: i <= step ? '100%' : '0%' }} transition={{ duration: 0.4, ease: EASE }} />
            </Box>
          ))}
        </Stack>
        <Button onClick={() => onFinish(false)} endIcon={<CloseRounded sx={{ fontSize: 16 }} />}
          sx={{ fontFamily: FONT_BODY, textTransform: 'none', fontSize: 12.5, color: TXT_DIM, '&:hover': { color: TXT, bgcolor: alpha('#fff', 0.04) } }}>
          Saltar
        </Button>
      </Stack>

      {/* contenido */}
      <Box sx={{ flex: 1, position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 2.5, md: 5 } }}>
        <AnimatePresence mode="wait">
          {/* INTRO */}
          {isIntro && (
            <MBox key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6, ease: EASE }}
              sx={{ textAlign: 'center', maxWidth: 600 }}>
              <Box sx={{ display: 'inline-block', mb: 3 }}><GustavoAura size={isMobile ? 96 : 124} /></Box>
              <Typography sx={{ fontFamily: FONT_TECH, fontSize: 11.5, letterSpacing: '0.34em', color: accentCL(), mb: 1.5 }}>TU ASISTENTE</Typography>
              <Typography component="h1" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 30, md: 46 }, lineHeight: 1.05, mb: 1.5 }}>
                Hola{name ? `, ${name}` : ''}.<br />Pedilo, y lo hago.
              </Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: { xs: 15, md: 17 }, color: TXT_DIM, lineHeight: 1.6, maxWidth: 460, mx: 'auto', mb: 4 }}>
                Soy Gustavo. En menos de un minuto te muestro 4 cosas que puedo hacer por vos hoy mismo — sin formularios, sin vueltas.
              </Typography>
              <Button onClick={next} endIcon={<ArrowForwardRounded />}
                sx={{ ...glassGreenSx(), fontSize: 15, px: 4, py: 1.3 }}>
                Mostrame
              </Button>
            </MBox>
          )}

          {/* CAPACIDAD */}
          {cap && (
            <MBox key={cap.key} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.55, ease: EASE }}
              sx={{ width: '100%', maxWidth: 1000, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 8 }, alignItems: 'center' }}>
              {/* texto */}
              <Box sx={{ order: { xs: 2, md: 1 }, textAlign: { xs: 'center', md: 'left' } }}>
                <Stack direction="row" spacing={1.25} alignItems="center" justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ mb: 2 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: em(0.14), border: `1px solid ${emL(0.3)}`, color: accentCL(), '& svg': { fontSize: 22 } }}>{cap.icon}</Box>
                  <Typography sx={{ fontFamily: FONT_TECH, fontSize: 11, letterSpacing: '0.24em', color: accentCL() }}>{cap.eyebrow}</Typography>
                </Stack>
                <Typography component="h2" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 26, md: 38 }, lineHeight: 1.1, mb: 1.5 }}>{cap.title}</Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: { xs: 15, md: 17 }, color: TXT_DIM, lineHeight: 1.6, maxWidth: 420, mx: { xs: 'auto', md: 0 }, mb: { xs: 0, md: 4 } }}>{cap.body}</Typography>
                <Button onClick={next} endIcon={<ArrowForwardRounded />}
                  sx={{ ...glassGreenSx(), display: { xs: 'none', md: 'inline-flex' }, fontSize: 14.5, px: 3.5, py: 1.2 }}>
                  Siguiente
                </Button>
              </Box>
              {/* mock */}
              <Box sx={{ order: { xs: 1, md: 2 }, display: 'flex', justifyContent: 'center' }}>
                <MockExchange cap={cap} active reduced={reduced} />
              </Box>
              {/* botón mobile */}
              <Button onClick={next} endIcon={<ArrowForwardRounded />}
                sx={{ ...glassGreenSx(), order: 3, display: { xs: 'inline-flex', md: 'none' }, justifySelf: 'center', fontSize: 14.5, px: 3.5, py: 1.2 }}>
                Siguiente
              </Button>
            </MBox>
          )}

          {/* RECAP — "Eso es solo el arranque" → invita a configurar */}
          {isRecap && (
            <MBox key="recap" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6, ease: EASE }}
              sx={{ textAlign: 'center', maxWidth: 600 }}>
              <Box sx={{ display: 'inline-block', mb: 3 }}><GustavoAura size={isMobile ? 84 : 104} /></Box>
              <Typography component="h2" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 28, md: 42 }, lineHeight: 1.05, mb: 2 }}>
                Eso es solo el arranque.
              </Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: { xs: 15, md: 17 }, color: TXT_DIM, lineHeight: 1.6, maxWidth: 480, mx: 'auto', mb: 4 }}>
                También agenda, busca clientes, te arma el resumen del día y mucho más. Hablale como a un colega — escribiendo o por audio. Dejémoslo listo en un paso.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="center">
                <Button onClick={next} variant="contained" startIcon={<WhatsAppIcon />}
                  sx={{ fontFamily: FONT_BODY, textTransform: 'none', fontWeight: 800, fontSize: 15, px: 4, py: 1.3, borderRadius: 2,
                    color: '#04140e', background: `linear-gradient(120deg, ${accentCL()}, ${accentC()})`, boxShadow: `0 12px 36px ${em(0.45)}`,
                    '&:hover': { background: `linear-gradient(120deg, ${accentCL()}, ${accentCL()})` } }}>
                  Configurar Gustavo
                </Button>
                <Button onClick={() => onFinish(false)}
                  sx={{ fontFamily: FONT_BODY, textTransform: 'none', fontWeight: 600, fontSize: 14, color: TXT_DIM, '&:hover': { color: TXT, bgcolor: alpha('#fff', 0.04) } }}>
                  Explorar por mi cuenta
                </Button>
              </Stack>
            </MBox>
          )}

          {/* WHATSAPP — cierre: configurar el número y arrancar a charlar */}
          {isWhatsapp && (
            <MBox key="whatsapp" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.55, ease: EASE }}
              sx={{ width: '100%', maxWidth: 560, textAlign: 'center' }}>
              <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(WA_GREEN, 0.16), border: `1px solid ${alpha(WA_GREEN, 0.35)}`, color: WA_GREEN, '& svg': { fontSize: 22 } }}>
                  <WhatsAppIcon />
                </Box>
                <Typography sx={{ fontFamily: FONT_TECH, fontSize: 11, letterSpacing: '0.24em', color: WA_GREEN }}>EN TU BOLSILLO</Typography>
              </Stack>
              <Typography component="h2" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 26, md: 38 }, lineHeight: 1.1, mb: 1.5 }}>
                Llevátelo a la calle
              </Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: { xs: 15, md: 17 }, color: TXT_DIM, lineHeight: 1.6, maxWidth: 460, mx: 'auto', mb: 3 }}>
                El mismo Gustavo te responde por WhatsApp. Te reconoce por tu número, así sabe que sos vos — sin claves ni vueltas.
              </Typography>
              <WhatsappStep />
              {waReady && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, maxWidth: 440, mx: 'auto' }}>
                  <Button
                    onClick={() => { window.open(waChatLink, '_blank', 'noopener,noreferrer'); onFinish(false); }}
                    variant="contained" startIcon={<WhatsAppIcon />}
                    sx={{ fontFamily: FONT_BODY, textTransform: 'none', fontWeight: 800, fontSize: 15, px: 3.5, py: 1.3, borderRadius: 2,
                      color: '#04140e', background: `linear-gradient(120deg, ${accentCL()}, ${accentC()})`, boxShadow: `0 12px 36px ${em(0.45)}`,
                      '&:hover': { background: `linear-gradient(120deg, ${accentCL()}, ${accentCL()})` } }}>
                    Empezar a usar Gustavo
                  </Button>
                  <Button onClick={() => onFinish(true)}
                    sx={{ fontFamily: FONT_BODY, textTransform: 'none', fontWeight: 600, fontSize: 14, color: TXT_DIM, '&:hover': { color: TXT, bgcolor: alpha('#fff', 0.04) } }}>
                    Usar más tarde
                  </Button>
                </Stack>
              )}
            </MBox>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}
